import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import { makeStyles } from "tss-react/mui";

// MUI Imports
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";

import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import ObjectSelection from "../../../../utils/ObjectSelection";
import { DATA_SOURCE, OBJECT_TYPE } from "../../../../../../../util/Data/DataModeling";
import { ButtonUI } from "../../../../../../Common";
import { OBJECT_TYPE_ROUTES } from "../../../../utils/JobData";
import { openInNewTab } from "../../../../../../../util/common";

const useStyles = makeStyles()((theme) => ({
  Alert: {
    marginTop: 12,
    paddingTop: 0,
    paddingBottom: 0,
    borderRadius: 20,
  },
}));

const DataSourceList = (props) => {
  const {
    tabType,
    state: dataSources,
    setState,
    selectAll,
    intermediate,
    handleRefresh,
    exceptReadOnly = false,
  } = props;

  const { classes } = useStyles();

  const dataPipeline = useSelector((state) => state.dataPipeline);
  const {
    editJob: { id, data },
  } = useMemo(() => dataPipeline, [dataPipeline]);
  const { Workspace_id } = useParams();

  const [loading, setLoading] = useState(false);

  const onRefresh = useCallback(async () => {
    setLoading(true);
    const objectList = await handleRefresh(
      [5, 6].includes(Number(data.type)) ? "Entity" : "DataSource",
      Number(data.type),
      Number(id)
    );

    //Setting selected refresh data sources
    if (objectList.length) {
      if (selectAll === true) setState(objectList.map((item) => ({ ...item, checked: true })));
      else
        setState((prevState) => {
          const idToChecked = {};
          prevState.forEach(({ id, checked = false }) => {
            idToChecked[id] = checked;
          });
          const updatedState = objectList.map((object) => ({
            ...object,
            checked: idToChecked[object?.id] || false,
          }));

          return updatedState;
        });
    }

    setLoading(false);
  }, [data.type, handleRefresh, id, selectAll, setState]);

  const handleSelectAllChange = useCallback(
    (event) => {
      const isChecked = event.target.checked;
      setState((prev) => prev.map((item) => ({ ...item, checked: isChecked })));
    },
    [setState]
  );

  const handleCheckboxChange = useCallback(
    (event, name) => {
      const isChecked = event.target.checked;
      setState((prev) => prev.map((item) => (item.name === name ? { ...item, checked: isChecked } : item)));
    },
    [setState]
  );

  return (
    <>
      <Grid container>
        <Grid size={12}>
          <Box display="flex">
            <Typography fontStyle="italic" sx={{ flexGrow: "1" }}>
              {tabType === "DataSets" &&
                `Select ${
                  DATA_SOURCE.plural
                } that this job will operate on (at least one ${DATA_SOURCE.singular.toLowerCase()} must be selected)`}
              {tabType === "Entities" &&
                `Select Entities that this job will operate on (at least one entity must be selected)`}
            </Typography>
            <ButtonUI
              endIcon={<OpenInNewIcon />}
              variant="contained"
              disabled={loading}
              color="info"
              style={{ width: "auto" }}
              onClick={() =>
                openInNewTab(`/my-workspaces/${Workspace_id}/${OBJECT_TYPE_ROUTES[tabType === "DataSets" ? 1 : 2]}/add`)
              }
            >
              {`Add ${tabType === "DataSets" ? DATA_SOURCE.singular : "Entity"}`}
            </ButtonUI>
          </Box>
        </Grid>
        <ObjectSelection
          selectAll={selectAll}
          intermediate={intermediate}
          objectList={dataSources}
          objectType={tabType === "DataSets" ? OBJECT_TYPE.DataSource : OBJECT_TYPE.Entity}
          handleSelectAllChange={handleSelectAllChange}
          handleCheckboxChange={handleCheckboxChange}
          handleRefresh={onRefresh}
          loading={loading}
        />
      </Grid>

      {exceptReadOnly && (
        <Box sx={{ width: "100%" }}>
          <Alert
            variant="outlined"
            icon={<InfoOutlinedIcon fontSize="inherit" />}
            className={classes.Alert}
            sx={{ width: "fit-content" }}
            severity="info"
          >
            Datasets that are set to 'Read-Only Data View' are not shown in this list.
          </Alert>
        </Box>
      )}
    </>
  );
};

DataSourceList.propTypes = {
  tabType: PropTypes.string.isRequired,
  selectAll: PropTypes.bool.isRequired,
  intermediate: PropTypes.bool.isRequired,
  state: PropTypes.array.isRequired,
  setState: PropTypes.func.isRequired,
  handleRefresh: PropTypes.func.isRequired,
};

export default React.memo(DataSourceList);
